export enum VisualPermissionsEnum {
    ASSETS_METADATA = 'ASSETS_METADATA',
    ROOM_TYPES = 'ROOM_TYPES',
    MEETING_ROOM_TYPES = 'MEETING_ROOM_TYPES',
    PHOTO_ORGANIZER = 'PHOTO_ORGANIZER',
    CREATE_ASSETS = 'CREATE',
    DELETE_ASSETS = 'DELETE',
    ENABLE_DISABLE_ASSETS = 'ENABLE_DISABLE',
    REORDER_ASSETS = 'REORDER',
    CROP_ASSETS = 'CROP',
    DOWNLOAD_ASSETS = 'DOWNLOAD_ASSETS',
    CUSTOM_CONTENT = 'CUSTOM_CONTENT',
}

export enum VisualPermissionsAccessTypeEnum {
    HIDE = 'HIDE',
    VIEW = 'VIEW',
    EDIT = 'EDIT',
    ACTIVE = 'ACTIVE',
}

export enum VisualPermissionKeysEnum {
    // ASSETS_METADATA permissions
    ASSETS_METADATA_VIEW = `${VisualPermissionsEnum.ASSETS_METADATA}_${VisualPermissionsAccessTypeEnum.VIEW}`,
    ASSETS_METADATA_EDIT = `${VisualPermissionsEnum.ASSETS_METADATA}_${VisualPermissionsAccessTypeEnum.EDIT}`,

    // ROOM_TYPES permissions
    ROOM_TYPES_VIEW = `${VisualPermissionsEnum.ROOM_TYPES}_${VisualPermissionsAccessTypeEnum.VIEW}`,
    ROOM_TYPES_EDIT = `${VisualPermissionsEnum.ROOM_TYPES}_${VisualPermissionsAccessTypeEnum.EDIT}`,

    // MEETING_ROOM_TYPES permissions
    MEETING_ROOM_TYPES_VIEW = `${VisualPermissionsEnum.MEETING_ROOM_TYPES}_${VisualPermissionsAccessTypeEnum.VIEW}`,
    MEETING_ROOM_TYPES_EDIT = `${VisualPermissionsEnum.MEETING_ROOM_TYPES}_${VisualPermissionsAccessTypeEnum.EDIT}`,

    // PHOTO_ORGANIZER permissions
    PHOTO_ORGANIZER_VIEW = `${VisualPermissionsEnum.PHOTO_ORGANIZER}_${VisualPermissionsAccessTypeEnum.VIEW}`,
    PHOTO_ORGANIZER_EDIT = `${VisualPermissionsEnum.PHOTO_ORGANIZER}_${VisualPermissionsAccessTypeEnum.EDIT}`,

    // CUSTOM_CONTENT permissions
    CUSTOM_CONTENT_VIEW = `${VisualPermissionsEnum.CUSTOM_CONTENT}_${VisualPermissionsAccessTypeEnum.VIEW}`,
    CUSTOM_CONTENT_EDIT = `${VisualPermissionsEnum.CUSTOM_CONTENT}_${VisualPermissionsAccessTypeEnum.EDIT}`,

    // CREATE_ASSETS permissions (child of ASSETS_METADATA)
    CREATE_ASSETS_ACTIVE = `${VisualPermissionsEnum.CREATE_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,

    // DELETE_ASSETS permissions (child of ASSETS_METADATA)
    DELETE_ASSETS_ACTIVE = `${VisualPermissionsEnum.DELETE_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,

    // ENABLE_DISABLE_ASSETS permissions (child of ASSETS_METADATA)
    ENABLE_DISABLE_ASSETS_ACTIVE = `${VisualPermissionsEnum.ENABLE_DISABLE_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,

    // REORDER_ASSETS permissions (child of ASSETS_METADATA)
    REORDER_ASSETS_ACTIVE = `${VisualPermissionsEnum.REORDER_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,

    // CROP_ASSETS permissions (child of ASSETS_METADATA)
    CROP_ASSETS_ACTIVE = `${VisualPermissionsEnum.CROP_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,

    // DOWNLOAD_ASSETS permissions (child of ASSETS_METADATA)
    DOWNLOAD_ASSETS_ACTIVE = `${VisualPermissionsEnum.DOWNLOAD_ASSETS}_${VisualPermissionsAccessTypeEnum.ACTIVE}`,
}

export interface VisualPermission {
    name: VisualPermissionsEnum;
    isImplemented?: boolean;
    possibleAccessTypes: VisualPermissionsAccessTypeEnum[];
    dependsOnAccessType?: VisualPermissionsAccessTypeEnum;
    children?: VisualPermission[];
}

export interface LocalVisualPermission extends Pick<VisualPermission, 'name' | 'possibleAccessTypes' | 'dependsOnAccessType'> {}

export interface VisualPermissionResponse {
    id?: string;
    userId?: string;
    userGroupId?: string;
    type: VisualPermissionsEnum;
    accessType: VisualPermissionsAccessTypeEnum;
    isImplemented: boolean;
    derivedVisualPermissions?: DerivedVisualPermissionResponse[];
}

export interface VisualPermissionUpdateInputGraph {
    id: string;
    userId?: string;
    userGroupId?: string;
    accessType: VisualPermissionsAccessTypeEnum;
    type: VisualPermissionsEnum;
    updatedDerivedVisualPermissions: DerivedVisualPermissionUpdateInputGraph[];
}

export interface VisualPermissionCreateInputGraph {
    userId?: string;
    userGroupId?: string;
    accessType: VisualPermissionsAccessTypeEnum;
    type: VisualPermissionsEnum;
    updatedDerivedVisualPermissions: DerivedVisualPermissionUpdateInputGraph[];
}

export interface DerivedVisualPermissionUpdateInputGraph {
    id: string;
    isActive: boolean;
    type: VisualPermissionsEnum;
}

export interface DerivedVisualPermissionResponse {
    id: string;
    type: VisualPermissionsEnum;
    isActive: boolean;
    isImplemented: boolean;
}

export const visualPermissionsSetup: LocalVisualPermission[] = [
    {
        name: VisualPermissionsEnum.ASSETS_METADATA,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.VIEW, VisualPermissionsAccessTypeEnum.EDIT],
    },
    {
        name: VisualPermissionsEnum.ROOM_TYPES,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.VIEW, VisualPermissionsAccessTypeEnum.EDIT],
    },
    {
        name: VisualPermissionsEnum.MEETING_ROOM_TYPES,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.VIEW, VisualPermissionsAccessTypeEnum.EDIT],
    },
    {
        name: VisualPermissionsEnum.PHOTO_ORGANIZER,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.VIEW, VisualPermissionsAccessTypeEnum.EDIT],
    },
    {
        name: VisualPermissionsEnum.CREATE_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.DELETE_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.ENABLE_DISABLE_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.REORDER_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.CROP_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.DOWNLOAD_ASSETS,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.ACTIVE],
        dependsOnAccessType: VisualPermissionsAccessTypeEnum.EDIT,
    },
    {
        name: VisualPermissionsEnum.CUSTOM_CONTENT,
        possibleAccessTypes: [VisualPermissionsAccessTypeEnum.VIEW, VisualPermissionsAccessTypeEnum.EDIT],
    },
];
